import React, {FC, useMemo, useState, useEffect} from 'react';
import {Layer, Source, useMap} from 'react-map-gl/maplibre';

import {ImageSourceSpecification, RasterLayerSpecification} from '@maplibre/maplibre-gl-style-spec';
import {CETEMAS_OVERLAYS, PRINCIPADO_OVERLAYS} from '@/config';
import {Project} from '@/domain/entities/Project';


const _convertTo3857 = (lng: number, lat: number) => {
  const x = lng * 20037508.34 / 180;
  const y = Math.log(Math.tan((90 + lat) * Math.PI / 360)) / (Math.PI / 180);
  const yMercator = y * 20037508.34 / 180;

  return {x: x, y: yMercator};
};

const _overlaySource = (
  url: string | Array<string>,
  [[xMin, yMin], [xMax, yMax]]: [number, number][], //bbox
  [_width, _height]: [number, number], //size
  transparent: boolean
): ImageSourceSpecification => {

  const {x: xMinMercator, y: yMinMercator} = _convertTo3857(xMin, yMin);
  const {x: xMaxMercator, y: yMaxMercator} = _convertTo3857(xMax, yMax);

  return {
    type: 'image',
    url: `${url}` +
      `format=${transparent ? 'image/png': 'image/jpeg'}&` +
      `transparent=${transparent}&` +
      `bbox=${xMinMercator},${yMinMercator},${xMaxMercator},${yMaxMercator}&` +
      `width=${_width}&` +
      `height=${_height}`,
    coordinates: [
      [xMin, yMax],
      [xMax, yMax],
      [xMax, yMin],
      [xMin, yMin],
    ]
  };
};

type OverlayProps = {
  project?: Project,
  overlayId: string,
  beforeId?: string,
  transparent?: boolean
};

const Overlay: FC<OverlayProps> = ({project, overlayId, beforeId, transparent = true}) => {

  const {current: map} = useMap();
  const [canvasBounds, setCanvasBounds] = useState<[number, number][] | undefined>(undefined);
  const [canvasSize, setCanvasSize] = useState<[number, number] | undefined>(undefined);

  const OVERLAYS_FOR_PROJECT = project ? CETEMAS_OVERLAYS(project?.zone.toLowerCase()) : [];
  const ALL_OVERLAYS = [...PRINCIPADO_OVERLAYS, ...OVERLAYS_FOR_PROJECT];

  const overlay = ALL_OVERLAYS.find(o => o.id === overlayId);

  if (!overlay) return null;

  useEffect(() => {
    console.log(`Setting up Overlay ${overlay.id}`);
    const callback = () => {
      if (map) setCanvasBounds(map.getBounds().toArray());
      if (map) setCanvasSize([map.getCanvas().width, map.getCanvas().height]);
    };

    if (map) {
      map.on('idle', callback);
      callback();
      console.log(`Set up Overlay ${overlay.id}`);
    }

    return () => {
      if (map) map.off('idle', callback);
    };
  }, [map]);

  const source = useMemo(() =>
    canvasBounds && canvasSize && _overlaySource(overlay.url, canvasBounds, canvasSize, transparent),
  [canvasBounds, canvasSize, transparent]);

  const layerStyle: RasterLayerSpecification = {
    id: overlay.id,
    type: 'raster',
    source: overlay.id,
    paint: {
      'raster-opacity': 0.75
    }
  };

  if (!source) return <></>;

  return <Source id={overlay.id} {...source}>
    <Layer {...layerStyle} beforeId={beforeId} />
  </Source>;
};

export default Overlay;